import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, useMediaQuery, useTheme, Snackbar, Alert,
} from '@mui/material';
import api from '../../services/api';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import UserOpsBar from '../../components/users/UserOpsBar';
import UserListTable from '../../components/users/UserListTable';
import UserFormDrawer from '../../components/users/UserFormDrawer';
import { USER_TYPE_LABELS, USER_TYPE_OPTIONS, resolveBlockedReason, type UserRow, type UserFormState } from '../../constants/userManagement';
import { downloadListCsv, rowsToCsv } from '../../utils/listExportCsv';
import { saveUser, getApiErrorMessage } from '../../services/userService';
import { listZones } from '../../services/zoneService';
import { isPartnerUserType } from '../../utils/opsEntityNav';

const USER_EXPORT_COLUMNS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'userType', label: 'User Type', getValue: (row: Record<string, unknown>) => USER_TYPE_LABELS[String(row.userType || '')] || row.userType },
  { key: 'isActive', label: 'Active', getValue: (row: Record<string, unknown>) => (row.isActive ? 'Yes' : 'No') },
  { key: 'isBlocked', label: 'Blocked', getValue: (row: Record<string, unknown>) => (row.isBlocked ? 'Yes' : 'No') },
  { key: 'blockedReason', label: 'Blocked Reason', getValue: (row: Record<string, unknown>) => (row.isBlocked ? resolveBlockedReason(row as unknown as UserRow) : '') },
  { key: 'isVerified', label: 'Verified', getValue: (row: Record<string, unknown>) => (row.isVerified ? 'Yes' : 'No') },
  { key: 'lastLogin', label: 'Last Login', getValue: (row: Record<string, unknown>) => row.lastLogin ? new Date(String(row.lastLogin)).toLocaleString() : '' },
  { key: 'createdAt', label: 'Registered', getValue: (row: Record<string, unknown>) => row.createdAt ? new Date(String(row.createdAt)).toLocaleString() : '' },
];

export default function UsersPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [blockTarget, setBlockTarget] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(() => {
    const fromUrl = searchParams.get('userType') || '';
    return USER_TYPE_OPTIONS.some((o) => o.value && o.value === fromUrl) ? fromUrl : '';
  });
  const registeredDate = searchParams.get('registeredDate') || '';
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [snack, setSnack] = useState('');

  useEffect(() => {
    const fromUrl = searchParams.get('userType') || '';
    const next = USER_TYPE_OPTIONS.some((o) => o.value && o.value === fromUrl) ? fromUrl : '';
    setTypeFilter((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  const setUserTypeFilter = (next: string) => {
    setTypeFilter(next);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (next) params.set('userType', next);
    else params.delete('userType');
    setSearchParams(params, { replace: true });
  };

  const { data: zonesData } = useQuery({
    queryKey: ['zones-list-mini'],
    queryFn: () => listZones({ limit: 100 }),
  });
  const zones = zonesData?.rows || [];

  const { data, isLoading } = useQuery({
    queryKey: ['users', typeFilter, statusFilter, search, page, pageSize, registeredDate],
    queryFn: async () => {
      const res = await api.get('/admin/users', {
        params: {
          page: page + 1,
          limit: pageSize,
          userType: typeFilter || undefined,
          search: search.trim() || undefined,
          registeredDate: registeredDate || undefined,
        },
      });
      return {
        rows: (res.data.data || []) as UserRow[],
        total: Number(res.data.meta?.total ?? res.data.data?.length ?? 0),
      };
    },
  });

  const allRows = data?.rows || [];
  const totalFromApi = data?.total ?? 0;

  const filteredRows = useMemo(() => {
    let list = allRows;
    if (typeFilter) {
      list = list.filter((r) => r.userType === typeFilter);
    }
    if (statusFilter === 'active') list = list.filter((r) => r.isActive && !r.isBlocked);
    else if (statusFilter === 'inactive') list = list.filter((r) => !r.isActive);
    else if (statusFilter === 'blocked') list = list.filter((r) => r.isBlocked);
    else if (statusFilter === 'unverified') list = list.filter((r) => !r.isVerified);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const text = [
          r.firstName, r.lastName, r.email, r.phone, r.userType,
        ].filter(Boolean).join(' ').toLowerCase();
        return text.includes(q);
      });
    }
    return list;
  }, [allRows, typeFilter, statusFilter, search]);

  const totalCount = statusFilter ? filteredRows.length : totalFromApi;

  const refreshUsers = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const saveMutation = useMutation({
    mutationFn: (form: UserFormState) => saveUser(form, editingUser?._id),
    onSuccess: () => {
      refreshUsers();
      setDrawerOpen(false);
      setEditingUser(null);
      setSaveError('');
      setSnack(editingUser ? 'User updated successfully' : 'User created successfully');
    },
    onError: (err) => setSaveError(getApiErrorMessage(err, 'Failed to save user')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.put(`/admin/users/${id}/status`, { isActive }),
    onSuccess: refreshUsers,
  });
  const blockMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.put(`/admin/users/${id}/block`, { reason }),
    onSuccess: () => {
      refreshUsers();
      setBlockTarget(null);
      setBlockReason('');
    },
  });
  const unblockMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/users/${id}/unblock`, {}),
    onSuccess: refreshUsers,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/users/${id}`),
    onSuccess: refreshUsers,
  });

  const openCreate = () => {
    setEditingUser(null);
    setSaveError('');
    setDrawerOpen(true);
  };
  const openEdit = (row: UserRow) => {
    setEditingUser(row);
    setSaveError('');
    setDrawerOpen(true);
  };

  const clearFilters = () => {
    setSearch('');
    setUserTypeFilter('');
    setStatusFilter('');
    setPage(0);
    if (registeredDate) {
      const params = new URLSearchParams(searchParams);
      params.delete('registeredDate');
      setSearchParams(params, { replace: true });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/users', {
        params: {
          page: 1,
          limit: 5000,
          userType: typeFilter || undefined,
          search: search.trim() || undefined,
          registeredDate: registeredDate || undefined,
        },
      });
      let exportRows = (res.data.data || []) as UserRow[];
      if (statusFilter === 'active') exportRows = exportRows.filter((r) => r.isActive && !r.isBlocked);
      else if (statusFilter === 'inactive') exportRows = exportRows.filter((r) => !r.isActive);
      else if (statusFilter === 'blocked') exportRows = exportRows.filter((r) => r.isBlocked);
      else if (statusFilter === 'unverified') exportRows = exportRows.filter((r) => !r.isVerified);
      const csv = rowsToCsv(exportRows as unknown as Record<string, unknown>[], USER_EXPORT_COLUMNS);
      downloadListCsv(csv, 'users');
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Administration"
        title="Users & Admins"
        subtitle={
          registeredDate
            ? `Users registered on ${registeredDate}. Date and time are listed in the Registered column.`
            : 'Manage tenant users, zone admins, partners, and staff. Registration date and time are shown for each account.'
        }
      />

      <UserOpsBar
        search={search}
        onSearchChange={(q) => { setSearch(q); setPage(0); }}
        typeFilter={typeFilter}
        onTypeFilterChange={setUserTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        totalCount={totalFromApi}
        filteredCount={filteredRows.length}
        onAdd={openCreate}
        onClearFilters={clearFilters}
        onExport={handleExport}
        exporting={exporting}
      />

      <UserListTable
        rows={filteredRows}
        loading={isLoading}
        onView={(row) => {
          if (row.userType === 'customer') {
            navigate(`/customers/${row._id}`, { state: { from: '/users' } });
          } else if (isPartnerUserType(row.userType)) {
            navigate(`/partners/${row._id}`, { state: { from: '/users' } });
          }
        }}
        onEdit={openEdit}
        onDelete={(row) => { if (window.confirm('Delete this user?')) deleteMutation.mutate(row._id); }}
        onToggleActive={(row) => statusMutation.mutate({ id: row._id, isActive: !row.isActive })}
        onBlock={(row) => setBlockTarget(row._id)}
        onUnblock={(row) => unblockMutation.mutate(row._id)}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
      />

      <UserFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingUser(null); setSaveError(''); }}
        editingUser={editingUser}
        zones={zones}
        loading={saveMutation.isPending}
        saveError={saveError}
        onSave={(form) => saveMutation.mutate(form)}
      />

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSnack('')} sx={{ width: '100%' }}>{snack}</Alert>
      </Snackbar>

      <Dialog
        open={!!blockTarget}
        onClose={() => setBlockTarget(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Block User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="Blocked by admin"
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBlockTarget(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => blockTarget && blockMutation.mutate({ id: blockTarget, reason: blockReason || 'Blocked by admin' })}
          >
            Block
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
